import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Mail, Send, Music4, BadgeCheck, Clock, MessageSquare, Package, ShieldCheck, BellRing, ExternalLink, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { PublicLangProvider, LangToggle, useT, apiErrorKey } from "@/lib/publicI18n";
import SocialShare from "@/components/SocialShare";

const STATUS = {
  approved: { key: "st_approved", cls: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10", Icon: BadgeCheck },
  annotated: { key: "st_annotated", cls: "text-amber-300 border-amber-500/40 bg-amber-500/10", Icon: MessageSquare },
  reminded: { key: "st_reminded", cls: "text-amber-200 border-amber-500/30 bg-amber-500/5", Icon: BellRing },
  pending: { key: "st_pending", cls: "text-cyan-200 border-cyan-500/30 bg-cyan-500/5", Icon: Clock },
  expired: { key: "st_expired", cls: "text-muted-foreground border-border bg-muted/20", Icon: Clock },
};

function RequestForm() {
  const { t, lang } = useT();
  const [email, setEmail] = useState(() => localStorage.getItem("mf_share_email") || "");
  const [busy, setBusy] = useState(false); const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await fetch(`${API}/portal/request`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: email.trim(), lang }) });
      if (!r.ok) throw new Error(t("invalid_email"));
      localStorage.setItem("mf_share_email", email.trim()); setDone(true);
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };
  if (done) return <div className="rounded-xl border border-cyan-500/40 bg-cyan-500/10 p-4 text-sm" data-testid="portal-request-done"><Mail className="inline mr-2 text-cyan-300" size={16} />{t("portal_req_done", { e: email })}</div>;
  return (
    <form onSubmit={submit} className="rounded-xl border border-border bg-card p-4 space-y-3" data-testid="portal-request-form">
      <p className="text-sm">{t("portal_req_intro")}</p>
      <div className="flex gap-2 flex-wrap">
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@studio.com" className="h-10 flex-1 min-w-[220px] rounded-md border border-border bg-background px-3 text-sm" data-testid="portal-request-email" />
        <button type="submit" disabled={busy || !email.trim()} className="h-10 inline-flex items-center gap-2 rounded-md bg-cyan-500 px-4 text-sm font-semibold text-black disabled:opacity-50 hover:bg-cyan-400" data-testid="portal-request-send">{busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {t("portal_req_send")}</button>
      </div>
    </form>
  );
}

function VersionRow({ v, theme }) {
  const { t, lang, locale } = useT();
  const d = (iso) => (iso ? new Date(iso).toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric" }) : "—");
  const st = STATUS[v.status] || STATUS.pending;
  return (
    <li className="rounded-lg border border-border bg-background/60 px-3 py-2 text-xs space-y-1" data-testid={`portal-version-${v.token}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="rounded bg-fuchsia-500/20 px-1.5 py-0.5 font-mono text-[10px] text-fuchsia-200">V{v.version}</span>
        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] ${st.cls}`} data-testid={`portal-status-${v.token}`}><st.Icon size={10} /> {t(st.key)}</span>
        <span className="text-muted-foreground">{t("received_on", { d: d(v.sent_at || v.created_at) })} · {v.expired ? t("expired") : t("valid_until", { d: d(v.expires_at) })} · {Math.round(v.seconds)} s</span>
        <span className="inline-flex items-center gap-1 text-amber-300" title={t("notes_tip")}><MessageSquare size={11} /> {v.notes.own}/{v.notes.total}{v.notes.replied ? ` · ${v.notes.replied} ${t("replies")}` : ""}{v.notes.resolved ? ` · ${v.notes.resolved} ${t("treated")}` : ""}</span>
        <span className="ml-auto flex items-center gap-3">
          {v.certificate_path && <a href={`${API}${v.certificate_path.replace(/^\/api/, "")}?lang=${lang}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-emerald-300 hover:text-emerald-100" data-testid={`portal-certificate-${v.token}`}><ShieldCheck size={12} /> {t("cert_short")}</a>}
          {v.bundle_path && <a href={`${API}${v.bundle_path.replace(/^\/api/, "")}?lang=${lang}`} className="inline-flex items-center gap-1 rounded-md bg-emerald-500 px-2 py-1 font-semibold text-black hover:bg-emerald-400" data-testid={`portal-bundle-${v.token}`}><Package size={12} /> {t("pack")}</a>}
          {!v.expired && <Link to={`${v.path}?lang=${lang}`} className="inline-flex items-center gap-1 text-cyan-300 hover:text-cyan-100" data-testid={`portal-open-${v.token}`}><ExternalLink size={12} /> {t("listen")}</Link>}
        </span>
      </div>
      {v.approval && <p className="text-emerald-200/90">{t("approved_line", { a: v.approval.author, d: d(v.approval.at) })}{v.approved_by_me ? ` ${t("you")}` : ""}{v.approval.message ? ` — « ${v.approval.message} »` : ""}</p>}
      {!v.expired && <SocialShare compact url={`${window.location.origin}${v.path}`} title={`${theme} V${v.version} — ${t("share_text")}`} lang={lang} testId={`portal-share-${v.token}`} track={{ kind: "cuesheet", ref: v.token }} />}
    </li>
  );
}

function PortalContent() {
  const { token } = useParams();
  const { t, lang, locale } = useT();
  const [data, setData] = useState(null); const [errorKey, setErrorKey] = useState(null);
  useEffect(() => { document.documentElement.lang = lang; }, [lang]);
  useEffect(() => {
    if (!token) return;
    fetch(`${API}/portal/${token}`).then(async (r) => { if (!r.ok) { setErrorKey(apiErrorKey(r.status)); return; } setData(await r.json()); }).catch(() => setErrorKey("invalid_link"));
  }, [token]); // once per token
  const error = errorKey ? t(errorKey) : null;
  const d = (iso) => (iso ? new Date(iso).toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric" }) : "—");
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-3xl mx-auto px-6 py-12 space-y-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="text-[10px] font-mono text-cyan-300 tracking-wider" data-testid="portal-tag">{t("portal_tag")}</p>
            <h1 className="font-display text-2xl sm:text-3xl font-bold mt-1" data-testid="portal-title">{t("portal_title")}</h1>
            {data && <p className="text-xs text-muted-foreground mt-1" data-testid="portal-meta">{t("portal_meta", { e: data.email, n: data.count, d: d(data.expires_at) })}</p>}
          </div>
          <LangToggle />
        </div>
        {!token && <RequestForm />}
        {token && error && <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-sm flex items-start gap-2" data-testid="portal-error"><ShieldAlert size={16} className="text-red-300 shrink-0 mt-0.5" /><div>{error}<div className="mt-3"><RequestForm /></div></div></div>}
        {token && !data && !error && <Loader2 className="animate-spin text-muted-foreground" />}
        {data && data.themes.length === 0 && <p className="text-sm text-muted-foreground" data-testid="portal-empty">{t("portal_empty")}</p>}
        {data && data.themes.map((th) => (
          <section key={th.theme_id} className="rounded-2xl border border-border bg-card p-4 space-y-3" data-testid={`portal-theme-${th.theme_id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <Music4 size={16} className="text-cyan-300" />
              <h2 className="font-display text-base md:text-lg font-bold">{th.theme}</h2>
              <span className="text-xs text-muted-foreground">{t("portal_versions", { n: th.versions.length, v: th.latest_version })}</span>
              {th.approved && <span className="ml-auto inline-flex items-center gap-1 text-xs text-emerald-300 font-semibold" data-testid={`portal-theme-approved-${th.theme_id}`}><BadgeCheck size={13} /> {t("approved")}</span>}
            </div>
            {th.description && <p className="text-xs text-muted-foreground line-clamp-2">{th.description}</p>}
            <ul className="space-y-1.5">{th.versions.map((v) => <VersionRow key={v.token} v={v} theme={th.theme} />)}</ul>
          </section>
        ))}
        <p className="text-[11px] text-muted-foreground pt-4">{t("powered")} <Link to="/" className="text-cyan-300 hover:underline">MusicFlow StudioAi</Link> {t("powered_tail")}</p>
      </div>
    </div>
  );
}

// Public client portal (FR/EN auto): every mock-up shared with an email (magic link), grouped by theme with versions, status, notes, certificates and bundles.
export default function PortalPage() {
  return <PublicLangProvider><PortalContent /></PublicLangProvider>;
}
